import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { SButton, SCard, SIcon, SNotification } from '@chargebee/sting-react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { FormatConditionValues } from '@bbkAdminComponents/optimizer/optimization-condition-utils';
import { ActionCardDetails } from '@bbkAdminComponents/optimizer/OptimizationActions';
import { OptimizationConfigHelper } from '@bbkAdminComponents/optimizer/OptimizationConfigHelper';
import { selectApp } from '@bbkAdminRedux/app/selectors';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import {
  ApplyChangesEnum,
  OptimizerJobType,
  ScheduledChangesHandlingEnum,
  type GetChangesForSubscriptionResponse,
  type GetJobsResponse,
  type Optimization,
} from '@bbkAdminRedux/rtkq/optimizations.slice';

// Re-export for backward compatibility
export {
  Chip,
  formatConditions,
  FormatConditionValues,
} from '@bbkAdminComponents/optimizer/optimization-condition-utils';

export type StatusFilter =
  | 'all'
  | 'draft'
  | 'in_progress'
  | 'scheduled'
  | 'completed'
  | 'failed'
  | 'cancelled';

/**
 * Helper function to normalize status to a common format.
 * Handles both Clockwork job statuses (SUBMITTED, PENDING, RUNNABLE, STARTING, RUNNING, SUCCEEDED, FAILED)
 * and optimization-level statuses (draft, scheduled, in_progress, completed, failed, cancelled)
 */
export const getNormalizedStatus = (
  status: string | undefined
): StatusFilter => {
  if (!status) return 'draft';

  // Normalize to uppercase and replace spaces with underscores for consistent comparison
  const normalized = status.toUpperCase().replace(/\s+/g, '_');

  // Handle Clockwork job statuses and optimization-level statuses
  if (
    normalized === 'SUBMITTED' ||
    normalized === 'PENDING' ||
    normalized === 'RUNNABLE' ||
    normalized === 'STARTING' ||
    normalized === 'RUNNING' ||
    normalized === 'IN_PROGRESS'
  ) {
    return 'in_progress';
  }
  if (
    normalized === 'SUCCEEDED' ||
    normalized === 'COMPLETED' ||
    normalized === 'SUCCESS'
  ) {
    return 'completed';
  }
  if (normalized === 'FAILED') {
    return 'failed';
  }
  if (normalized === 'SCHEDULED') {
    return 'scheduled';
  }
  if (normalized === 'CANCELLED') {
    return 'cancelled';
  }
  return 'draft';
};

/**
 * Gets the latest execution job from jobs data.
 * Returns the job_id of the latest execution job, or undefined if none exists.
 */
export const getLatestExecutionJobId = (
  jobsData: GetJobsResponse | undefined
): string | undefined => {
  if (!jobsData?.collection) return undefined;

  const executionJobs = jobsData.collection.filter(
    (job) => job.job_type === OptimizerJobType.EXECUTION
  );
  if (executionJobs.length === 0) return undefined;

  // Sort by created_at descending to get the latest
  const sorted = [...executionJobs].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });

  return sorted[0]?.job_id;
};

const CHARGEBEE_HOST = 'chargebee.com';
const HTTP_SCHEME = 'http://';
const HTTPS_SCHEME = 'https://';

export const useChargebeeSiteName = (appKey: string): string | undefined => {
  const billingIntegration = useSelectBillingIntegration({ appKey });
  return billingIntegration?.required_input?.find((x) => x.name === 'site')
    ?.value;
};

export const useChargebeeSiteDomain = (): string | undefined => {
  const app = useSelector(selectApp);
  return app?.cb_site_domain;
};

export const normalizeCbSiteDomain = (
  cbSiteDomain: string | undefined
): string | undefined => {
  const trimmedDomain = cbSiteDomain?.trim();

  if (!trimmedDomain) {
    return undefined;
  }

  const normalizedDomain =
    trimmedDomain.startsWith(HTTP_SCHEME) ||
    trimmedDomain.startsWith(HTTPS_SCHEME)
      ? trimmedDomain
      : `${HTTPS_SCHEME}${trimmedDomain}`;

  return normalizedDomain.replace(/\/+$/, '');
};

export const getCbSubscriptionPageUrl = ({
  cbSiteDomain,
  chargebeeSiteName,
  subscriptionId,
}: {
  cbSiteDomain?: string;
  chargebeeSiteName?: string;
  subscriptionId?: string;
}): string | undefined => {
  const normalizedCbSiteDomain = normalizeCbSiteDomain(cbSiteDomain);

  if (!subscriptionId) {
    return undefined;
  }

  if (normalizedCbSiteDomain) {
    return `${normalizedCbSiteDomain}/d/subscriptions/${subscriptionId}`;
  }

  const normalizedSiteName = chargebeeSiteName?.trim();

  if (!normalizedSiteName) {
    return undefined;
  }

  return `https://${normalizedSiteName}.${CHARGEBEE_HOST}/d/subscriptions/${subscriptionId}`;
};

export const openCbSubscriptionPage = (
  chargebeeSiteName: string | undefined,
  subscriptionId: string,
  cbSiteDomain?: string
) => {
  const subscriptionPageUrl = getCbSubscriptionPageUrl({
    cbSiteDomain,
    chargebeeSiteName,
    subscriptionId,
  });

  if (!subscriptionPageUrl) {
    console.warn(
      'Cannot open Chargebee subscription page: missing site destination or subscription ID'
    );
    return;
  }

  window.open(subscriptionPageUrl, '_blank');
};

export const useHandleViewSubscription = (appKey: string) => {
  const chargebeeSiteName = useChargebeeSiteName(appKey);
  const chargebeeSiteDomain = useChargebeeSiteDomain();

  return (subscriptionId: string) => {
    if (!subscriptionId) {
      return;
    }

    const subscriptionPageUrl = getCbSubscriptionPageUrl({
      cbSiteDomain: chargebeeSiteDomain,
      chargebeeSiteName,
      subscriptionId,
    });

    if (!subscriptionPageUrl) {
      BlackToastTrigger({
        content: 'Please add a Chargebee site name to the billing integration',
        type: 'error',
      });
      return;
    }

    window.open(subscriptionPageUrl, '_blank');
  };
};

export const getBillingAlignmentText = (
  type: GetChangesForSubscriptionResponse['billing_alignment']
) => {
  switch (type) {
    case 'immediately':
      return 'Immediately';
    case 'end_of_term':
      return 'On Next Renewal';
    case 'specific_date':
      return 'On Specific Date';
    case 'billing_cycles':
      return 'In _ Billing Cycles';
    default:
      return 'Unknown';
  }
};

export class ScheduledChangesHelper {
  static formatDate(
    epochMs: number | null | undefined,
    timezone?: string
  ): string {
    if (epochMs == null || epochMs === 0) return '';
    const ms = epochMs < 1e12 ? epochMs * 1000 : epochMs;
    const m = timezone ? moment(ms).tz(timezone) : moment(ms);
    return m.format('DD MMM YYYY, HH:mm z');
  }

  static getWarning(
    count: number,
    type: ScheduledChangesHandlingEnum | undefined,
    applyChanges: ApplyChangesEnum | undefined
  ): string {
    const prefix = `${count} existing scheduled change${count !== 1 ? 's' : ''} detected.`;
    if (applyChanges === ApplyChangesEnum.END_OF_TERM) {
      return `${prefix} These will be automatically discarded when the new changes are applied at renewal.`;
    }
    switch (type) {
      case ScheduledChangesHandlingEnum.DISCARD_EXISTING_SCHEDULES:
        return `${prefix} These will be discarded and replaced with the new pricing changes.`;
      case ScheduledChangesHandlingEnum.RETAIN_EXISTING_SCHEDULES:
        return `${prefix} These will be retained.`;
      case ScheduledChangesHandlingEnum.SKIP_CONFLICTING_SUBSCRIBERS:
        return `${prefix} ${
          count === 1 ? 'This subscription' : 'These subscriptions'
        } will be skipped when the optimization is applied.`;
      default:
        return prefix;
    }
  }

  static getDiscardModalCallout(): string {
    return 'These scheduled changes will be cancelled and will not take effect.';
  }

  static getRetainModalCallout(
    activationDate: number | null | undefined,
    timezone?: string
  ): string {
    const dateStr = ScheduledChangesHelper.formatDate(activationDate, timezone);
    return dateStr
      ? `These scheduled changes will be retained and will activate on ${dateStr}.`
      : 'These scheduled changes will be retained.';
  }

  static getHandlingDescription(
    type: ScheduledChangesHandlingEnum | undefined,
    applyChanges: ApplyChangesEnum | undefined
  ): string {
    const applyAt =
      applyChanges === ApplyChangesEnum.END_OF_TERM
        ? 'at renewal'
        : 'immediately';
    switch (type) {
      case ScheduledChangesHandlingEnum.DISCARD_EXISTING_SCHEDULES:
        return (
          'The optimization is set to discard existing scheduled changes for this subscription. These are the changes that will be scheduled for this subscription when the optimization is applied ' +
          applyAt +
          '.'
        );
      case ScheduledChangesHandlingEnum.RETAIN_EXISTING_SCHEDULES:
        return (
          'The optimization is set to retain existing scheduled changes for this subscription. These are the changes that will be scheduled for this subscription when the optimization is applied ' +
          applyAt +
          '.'
        );
      case ScheduledChangesHandlingEnum.SKIP_CONFLICTING_SUBSCRIBERS:
        return (
          'The optimization is set to skip subscriptions that already have changes scheduled. This subscription will be skipped when the optimization is applied ' +
          applyAt +
          ', and these existing changes will be scheduled for the subscription.'
        );
      default:
        return 'The optimization may have an impact on the existing scheduled changes for this subscription. These are the changes that will be scheduled for this subscription.';
    }
  }

  /**
   * Scheduled-change detail modal only applies when retaining schedules on immediate apply.
   * {@code apply_changes} is optional on optimization configurations; omitted values match the
   * product default {@link ApplyChangesEnum.IMMEDIATELY} used elsewhere in the optimizer UI.
   */
  static isRetainScheduledChangesImmediateCase(
    scheduledChangesHandling: ScheduledChangesHandlingEnum | undefined,
    applyChanges: ApplyChangesEnum | undefined
  ): boolean {
    const effectiveApplyChanges = applyChanges ?? ApplyChangesEnum.IMMEDIATELY;
    return (
      scheduledChangesHandling ===
        ScheduledChangesHandlingEnum.RETAIN_EXISTING_SCHEDULES &&
      effectiveApplyChanges === ApplyChangesEnum.IMMEDIATELY
    );
  }
}

const EditButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <SButton
    icon={<SIcon className="size-4" name="pencil" />}
    iconPosition="left"
    size="small"
    variant="primary-ghost"
    onClick={onClick}
  >
    Edit
  </SButton>
);

const ReviewSummaryCard: React.FC<{
  title: string;
  onEdit?: () => void;
  showEdit: boolean;
  children: React.ReactNode;
}> = ({ title, onEdit, showEdit, children }) => (
  <SCard
    background="white"
    padding="regular"
    className="!tw-border !tw-border-neutral-200"
  >
    <SCard.Header
      title={title}
      className="tw-border-b tw-border-neutral-100"
      primaryAction={showEdit ? <EditButton onClick={onEdit} /> : undefined}
    />
    <SCard.Content className="!tw-pt-md">{children}</SCard.Content>
  </SCard>
);

export const OverviewContent: React.FC<{
  optimization: Partial<Optimization>;
  isPreviewPage: boolean;
  onEditAudience?: () => void;
  onEditActions?: () => void;
  onEditConfig?: () => void;
}> = ({
  optimization,
  isPreviewPage,
  onEditAudience,
  onEditActions,
  onEditConfig,
}) => {
  const applyImmediately =
    (optimization.configurations?.apply_changes ??
      ApplyChangesEnum.IMMEDIATELY) === ApplyChangesEnum.IMMEDIATELY;
  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-md">
      {isPreviewPage && (
        <SNotification
          description="We'll show you a sample of affected subscribers so you can verify your rules are working correctly before publishing to all subscribers."
          title="Preview sample changes before publishing?"
          variant="info"
          hasBorder
        />
      )}

      <ReviewSummaryCard
        title="Audience"
        showEdit={isPreviewPage}
        onEdit={onEditAudience}
      >
        <div className="tw-text-sm tw-text-neutral-700">
          {optimization.audience && (
            <FormatConditionValues condition={optimization.audience} />
          )}
        </div>
      </ReviewSummaryCard>

      <ReviewSummaryCard
        title="Actions"
        showEdit={isPreviewPage}
        onEdit={onEditActions}
      >
        <div className="tw-text-sm tw-text-neutral-700">
          {optimization.actions && (
            <ActionCardDetails actions={optimization.actions} />
          )}
        </div>
      </ReviewSummaryCard>

      <ReviewSummaryCard
        title="Configurations"
        showEdit={isPreviewPage}
        onEdit={onEditConfig}
      >
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
          <div className="tw-flex tw-flex-col">
            <div className="tw-mb-1 tw-text-sm tw-text-neutral-600">
              Changes take effect
            </div>
            <div className="tw-mb-1 tw-font-medium tw-text-neutral-900">
              {OptimizationConfigHelper.getApplyChangesLabel(
                optimization.configurations?.apply_changes ??
                  ApplyChangesEnum.IMMEDIATELY
              )}
            </div>
            {applyImmediately && (
              <div>
                <div className="tw-mb-1 tw-mt-md tw-text-sm tw-text-neutral-600">
                  Scheduled Change Handling
                </div>
                <div className="tw-mb-1 tw-font-medium tw-text-neutral-900">
                  {OptimizationConfigHelper.getChangeHandlingLabel(
                    optimization.configurations?.scheduled_changes_handling
                  )}
                </div>
              </div>
            )}
          </div>
          {applyImmediately && (
            <div>
              <div className="tw-mb-1 tw-text-sm tw-text-neutral-600">
                Mid-cycle billing options
              </div>
              <div className="tw-mb-1 tw-font-medium tw-text-neutral-900">
                {OptimizationConfigHelper.getProrationLabel(
                  optimization.configurations?.mid_cycle_billing
                )}
              </div>
            </div>
          )}
        </div>
      </ReviewSummaryCard>
    </div>
  );
};

const getVisiblePageNumbers = (currentPage: number, totalPages: number) => {
  const pages: (number | '...')[] = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else if (currentPage >= totalPages - 3) {
    for (let i = totalPages - 4; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    const start = Math.max(1, currentPage - 1);
    const end = Math.min(start + 1, totalPages - 2);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    pages.push('...');
    pages.push(totalPages - 1);
    pages.push(totalPages);
  }
  return pages;
};

export const OptimizationPaginationFooter: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  additionalClassName: string;
  itemsLabel?: string;
  setCurrentPage: (page: number) => void;
}> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  setCurrentPage,
  additionalClassName,
  itemsLabel,
}) => {
  const visiblePageNumbers = getVisiblePageNumbers(currentPage, totalPages);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);
  return (
    <div
      className={`${additionalClassName} tw-flex tw-flex-shrink-0 tw-items-center tw-justify-between tw-border-t tw-border-neutral-200 tw-bg-white tw-py-sm`}
    >
      <div className="tw-text-sm tw-font-normal tw-text-neutral-500">
        Showing {startIndex} - {endIndex} of {totalItems}
        {itemsLabel ? ` ${itemsLabel}` : ''}
      </div>
      <div className="tw-flex !tw-justify-items-end tw-gap-2">
        <SButton
          variant="primary-ghost"
          icon={<SIcon name="chevron-left" size={14} />}
          iconPosition="left"
          disabled={currentPage === 1}
          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          className="!tw-border-0 !tw-bg-white !tw-p-0"
        >
          Previous
        </SButton>
        {visiblePageNumbers.map((page, index) => {
          return (
            <SButton
              key={`page-${page}`}
              variant={
                page === '...'
                  ? 'neutral-ghost'
                  : currentPage === page
                    ? 'primary-outline'
                    : 'neutral'
              }
              onClick={() => typeof page === 'number' && setCurrentPage(page)}
            >
              {page}
            </SButton>
          );
        })}
        <SButton
          variant="primary-ghost"
          icon={<SIcon name="chevron-right" size={14} />}
          iconPosition="right"
          disabled={currentPage === totalPages}
          onClick={() =>
            currentPage < totalPages && setCurrentPage(currentPage + 1)
          }
          className="!tw-border-0 !tw-bg-white !tw-p-0"
        >
          Next
        </SButton>
      </div>
    </div>
  );
};
